import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import env from '../../config/env';

interface Violation {
  id: number;
  violation_type: string;
  status: string;
  note: string | null;
  created_at: string;
  ad: {
    id: number;
    title: string;
    ad_category: { id: number; title: string } | null;
  } | null;
}

const formatDate = (dateStr: string) => {
  const d = new Date(dateStr);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}.${month}.${year}`;
};

const violationTypeAr: Record<string, string> = {
  'policy non-compliance': 'مخالفة السياسات',
};

const statusAr: Record<string, string> = {
  'under review':   'قيد المراجعة',
  'warning issued': 'تم إصدار تحذير',
  'restricted':     'مقيّد',
};

const formatViolationType = (type: string, isAr: boolean) =>
  isAr ? (violationTypeAr[type?.toLowerCase()] ?? type) : type;

const formatViolationStatus = (status: string, isAr: boolean) =>
  isAr ? (statusAr[status?.toLowerCase()] ?? status) : status;

const affectedContent = (v: Violation) => {
  if (!v.ad) return '—';
  const title = v.ad.title || `Ad #${v.ad.id}`;
  const cat = v.ad.ad_category?.title;
  return cat ? `${title} / ${cat}` : title;
};

interface ContentProps { violations: Violation[]; isAr: boolean; }

const NoViolations: React.FC<{ isAr: boolean }> = ({ isAr }) => (
  <div style={styles.body}>
    <h2 style={{ ...styles.statusTitle, color: '#396A54' }}>
      {isAr ? 'لم يتم رصد أي مخالفات' : 'No Violations Detected'}
    </h2>
    <p style={styles.text}>
      {isAr
        ? 'حسابك في وضع جيد. لم نرصد أي مخالفات لسياسات المنصة.'
        : 'Your account is in good standing. We have not detected any policy violations.'}
    </p>
    <p style={styles.text}>
      {isAr
        ? 'استمر في اتباع إرشادات فِصالة للحفاظ على وضعك الموثوق والوصول المستمر إلى جميع المميزات.'
        : 'Keep following Fesilah guidelines to maintain your trusted status and uninterrupted access to all features.'}
    </p>
    <ul style={{ paddingLeft: 0, paddingRight: 0, listStyle: 'none', marginTop: 16 }}>
      <li style={styles.bulletItem}>
        - &nbsp;{isAr ? 'حالة الحساب: ' : 'Account Status: '}
        <span style={{ color: '#396A54', fontWeight: 600 }}>{isAr ? 'نشط' : 'Active'}</span>
      </li>
      <li style={styles.bulletItem}>
        - &nbsp;{isAr ? 'لا توجد قيود مطبقة' : 'No restrictions applied'}
      </li>
    </ul>
  </div>
);

const WithViolations: React.FC<ContentProps> = ({ violations, isAr }) => (
  <div style={styles.body}>
    <h3 style={{ ...styles.statusTitle, color: '#c62828' }}>
      {isAr ? 'تم رصد مخالفات للسياسات' : 'Policy Violations Detected'}
    </h3>
    <p style={styles.text}>
      {isAr
        ? 'لقد رصدنا مخالفة أو أكثر مرتبطة بحسابك. قد تكون بعض الميزات مقيدة مؤقتًا حسب خطورة المخالفة.'
        : 'We have identified one or more violations associated with your account. Some features may be temporarily restricted depending on the severity of the violation.'}
    </p>

    <p style={styles.sectionLabel}>{isAr ? 'ماذا يمكنك أن تفعل؟' : 'What You Can Do?'}</p>
    <p style={styles.text}>
      {isAr
        ? 'يرجى مراجعة محتواك والتأكد من الامتثال لسياسات فِصالة.'
        : 'Please review your content and ensure compliance with Fesilah policies.'}
    </p>
    <p style={styles.text}>
      {isAr
        ? 'إذا كنت تعتقد أن هذا الإجراء اتُّخذ بالخطأ، يمكنك التواصل معنا (support@fesilah.com) للتوضيح.'
        : 'If you believe this action was taken in error, you may contact us (support@fesilah.com) for clarification.'}
    </p>

    <p style={styles.sectionLabel}>{isAr ? 'إشعار مهم' : 'Important Notice'}</p>
    <p style={styles.text}>
      {isAr
        ? 'المخالفات المتكررة قد تؤدي إلى قيود إضافية.'
        : 'Repeated violations may result in further restrictions.'}
    </p>

    <p style={styles.sectionLabel}>{isAr ? 'تفاصيل المخالفة' : 'Violation Details'}</p>
    <div style={styles.divider} />

    {violations.map((v, i) => (
      <div key={v.id}>
        <div style={styles.violationRow}>
          <p style={styles.violationText}>{isAr ? 'نوع المخالفة: ' : 'Type of Violation: '}{formatViolationType(v.violation_type, isAr)}</p>
          <p style={styles.violationText}>{isAr ? 'المحتوى المتأثر: ' : 'Affected Content: '}{affectedContent(v)}</p>
          <p style={styles.violationText}>{isAr ? 'التاريخ: ' : 'Date: '}{formatDate(v.created_at)}</p>
          <p style={styles.violationText}>{isAr ? 'الحالة: ' : 'Status: '}{formatViolationStatus(v.status, isAr)}</p>
        </div>
        {i < violations.length - 1 && <div style={styles.rowDivider} />}
      </div>
    ))}
  </div>
);

const ViolationsPage: React.FC = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const userId = params.get('user_id');
  const isAr = params.get('language') === 'ar';

  const [violations, setViolations] = useState<Violation[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.documentElement.dir = isAr ? 'rtl' : 'ltr';
  }, [isAr]);

  useEffect(() => {
    if (!userId) { setLoading(false); return; }
    axios
      .get(`${env.apiUrl}/getUserViolations`, { params: { user_id: userId } })
      .then((res) => {
        if (res.data.status) setViolations(res.data.data || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [userId]);

  return (
    <div style={{ ...styles.container, direction: isAr ? 'rtl' : 'ltr' }}>
      {loading ? (
        <p style={styles.loadingText}>{isAr ? 'جارٍ التحميل…' : 'Loading…'}</p>
      ) : violations.length > 0 ? (
        <WithViolations violations={violations} isAr={isAr} />
      ) : (
        <NoViolations isAr={isAr} />
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    fontFamily: "'IBM Plex Sans Arabic', 'Segoe UI', Arial, sans-serif",
    backgroundColor: '#fff',
    minHeight: '100vh',
    padding: '24px 20px',
    boxSizing: 'border-box',
    maxWidth: 480,
    margin: '0 auto',
  },
  body: {
    paddingLeft: 4,
  },
  statusTitle: {
    fontSize: 18,
    fontWeight: 800,
    marginBottom: 14,
    marginTop: 0,
    textAlign: 'center',
  },
  text: {
    fontSize: 13,
    color: '#333',
    marginBottom: 10,
    lineHeight: 1.7,
  },
  bulletItem: {
    fontSize: 13,
    color: '#333',
    lineHeight: 1.8,
  },
  sectionLabel: {
    fontSize: 13,
    fontWeight: 700,
    color: '#111',
    marginBottom: 4,
    marginTop: 14,
  },
  divider: {
    borderBottom: '1px solid #ddd',
    marginBottom: 12,
  },
  violationRow: {
    paddingBottom: 10,
    paddingTop: 4,
  },
  violationText: {
    fontSize: 13,
    color: '#333',
    margin: '2px 0',
    lineHeight: 1.6,
  },
  rowDivider: {
    borderBottom: '1px solid #eee',
    marginBottom: 10,
  },
  loadingText: {
    fontSize: 14,
    color: '#666',
    textAlign: 'center',
    marginTop: 40,
  },
};

export default ViolationsPage;
